// axios-config.js
import axios from 'axios';

const apiEndpoint = process.env.REACT_APP_SERVER || '/api/';
const CSRF_COOKIE_NAME = 'csrftoken';
const CSRF_HEADER_NAME = 'X-CSRFToken';
const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);
const HARD_BLOCK_CODES = ['service_disabled', 'account_suspended', 'account_disabled', 'account_archived'];
const BILLING_BLOCK_CODES = ['trial_expired', 'payment_due', 'grace_period', 'subscription_inactive'];
const AUTH_STORAGE_KEYS = [
  'userId',
  'userName',
  'userEmail',
  'user',
  'remember',
  'integrations',
  'providers',
  'permissions',
  'company',
  'companies',
  'profile',
  'membership',
  'activeIntegration',
  'workspace'
];

let csrfBootstrapPromise = null;

const getCookieValue = name => {
  if (typeof document === 'undefined') {
    return '';
  }

  const cookiePrefix = `${name}=`;
  const cookieEntry = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(cookiePrefix));

  return cookieEntry ? decodeURIComponent(cookieEntry.slice(cookiePrefix.length)) : '';
};

const ensureCsrfCookie = async () => {
  const existingToken = getCookieValue(CSRF_COOKIE_NAME);
  if (existingToken) {
    return existingToken;
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = axios
      .get(`${apiEndpoint}login/csrf`, {
        withCredentials: true,
        xsrfCookieName: CSRF_COOKIE_NAME,
        xsrfHeaderName: CSRF_HEADER_NAME
      })
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }

  await csrfBootstrapPromise;
  return getCookieValue(CSRF_COOKIE_NAME);
};

const clearStoredAuthState = () => {
  if (typeof window === 'undefined') {
    return;
  }

  AUTH_STORAGE_KEYS.forEach(key => {
    window.localStorage.removeItem(key);
  });
};

const isAuthRequest = requestUrl =>
  requestUrl.includes('login/login') ||
  requestUrl.includes('login/logout') ||
  requestUrl.includes('login/csrf');

const isLoginRedirectResponse = response => {
  const responseUrl = response?.request?.responseURL;
  if (!responseUrl || typeof window === 'undefined') {
    return false;
  }

  try {
    const parsedUrl = new URL(responseUrl, window.location.origin);
    return parsedUrl.pathname === '/login';
  } catch (error) {
    return false;
  }
};

const forceClientLogout = () => {
  clearStoredAuthState();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

const readStoredJson = key => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const isStoredCompanyOwner = () => {
  const user = readStoredJson('user') || {};
  const company = readStoredJson('company') || {};
  return Number(user?.id) === Number(company?.owner_user_id);
};

const redirectForBlockedAccount = code => {
  if (typeof window === 'undefined') {
    return;
  }

  if (HARD_BLOCK_CODES.includes(code)) {
    if (!window.location.pathname.includes('/trial-expired')) {
      window.location.assign('/trial-expired');
    }
    return;
  }

  if (!BILLING_BLOCK_CODES.includes(code)) {
    return;
  }

  const targetPath = isStoredCompanyOwner()
    ? '/subscription/checkout'
    : '/trial-expired';

  if (window.location.pathname !== targetPath) {
    window.location.assign(targetPath);
  }
};

const axiosInstance = axios.create({
  baseURL: apiEndpoint,
  withCredentials: true,
  xsrfCookieName: CSRF_COOKIE_NAME,
  xsrfHeaderName: CSRF_HEADER_NAME
});

axiosInstance.interceptors.request.use(async config => {
  const method = String(config.method || 'get').toLowerCase();
  const requestUrl = String(config.url || '');

  if (SAFE_METHODS.has(method) || requestUrl.includes('login/csrf')) {
    return config;
  }

  const csrfToken = await ensureCsrfCookie();
  if (csrfToken) {
    config.headers = {
      ...config.headers,
      [CSRF_HEADER_NAME]: csrfToken
    };
  }

  return config;
});

// Intercept 401 responses to force logout across all tabs
axiosInstance.interceptors.response.use(
  response => {
    const requestUrl = String(response?.config?.url || '');
    if (!isAuthRequest(requestUrl) && isLoginRedirectResponse(response)) {
      forceClientLogout();
      return Promise.reject(new Error('Authentication required'));
    }
    return response;
  },
  error => {
    const requestUrl = String(error?.config?.url || '');

    if (error.response && !isAuthRequest(requestUrl) && isLoginRedirectResponse(error.response)) {
      forceClientLogout();
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !isAuthRequest(requestUrl)
    ) {
      forceClientLogout();
    }
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.code
    ) {
      redirectForBlockedAccount(error.response.data.code);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
